import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Brain,
  RefreshCw,
  Target,
  Loader2,
  Zap,
  Globe2,
  Users,
} from "lucide-react";
import { productList } from "@/data/products";
import MandalaRing from "@/components/MandalaRing";
import { subscribeBeta } from "@/lib/subscribe";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.1 },
  }),
};

/* ─── Stats row data ─────────────────────────────────── */
const stats = [
  { value: "1-to-1", label: "Always personalised" },
  { value: "2", label: "School products, one loop" },
  { value: "12+", label: "Indian languages in Padhlo" },
  { value: "Nov '26", label: "Closed beta" },
];

/* ─── Why it's different ─────────────────────────────── */
const differentiators = [
  {
    icon: Brain,
    title: "Sense → Predict → Correct",
    body: "Every CognitLoop product runs the same cognitive loop. The agent builds a model of what you understand right now, predicts where you'll break, and corrects exactly that — not a generic explanation, your specific gap.",
  },
  {
    icon: RefreshCw,
    title: "Mastery gates, not time gates",
    body: "You don't move on because you spent 20 minutes on a topic. You move on when a diagnostic proves you can apply it. Progress is earned, which means it's real.",
  },
  {
    icon: Target,
    title: "Syllabus-locked, not syllabus-adjacent",
    body: "Every product is anchored to a real curriculum — Common Core, AP, NCERT, board syllabi. The agent can't hallucinate a lesson plan because the plan comes from the standard, not a prompt.",
  },
  {
    icon: Zap,
    title: "Agentic, not reactive",
    body: "A chatbot waits for your question and answers it. An agent plans your week, sets the context, walks you through it, then closes the loop with a diagnostic before you even think to ask.",
  },
  {
    icon: Globe2,
    title: "Same loop, two school systems",
    body: "A Class 9 student in Chennai thinking in Tamil. A school student in Toronto or London on Common Core or IB. Same mastery loop — different syllabus, language, and regulations.",
  },
  {
    icon: Users,
    title: "Teachers in the loop, not replaced",
    body: "CognitLoop's community model puts 25% of platform revenue into a quality-weighted corpus for educators. The AI handles scale; the teachers handle depth. Both get paid for what they're actually good at.",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await subscribeBeta(email, "home");
      toast.success("You're in. We'll be in touch when beta opens.");
      setEmail("");
    } catch (err) {
      toast.error(err?.message || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="home-page">

      {/* ════════════════════════════════════════
          HERO
          ════════════════════════════════════════ */}
      <section className="relative overflow-hidden pt-32 pb-12 sm:pt-44 sm:pb-20">
        {/* Grid texture */}
        <div className="absolute inset-0 grid-pattern" aria-hidden="true" />

        {/* Warm saffron glow — nods to the vedic palette */}
        <div
          className="absolute -top-24 right-0 h-[520px] w-[520px] rounded-full bg-gradient-to-bl from-orange-200/30 via-amber-100/20 to-transparent blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute -top-16 left-1/4 h-[360px] w-[600px] rounded-full bg-gradient-to-r from-blue-200/30 via-indigo-200/25 to-transparent blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left — copy */}
            <div>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-sm font-medium text-slate-600"
              >
                <Sparkles className="h-4 w-4 text-orange-500" />
                Two products. One loop. Closed beta November 2026.
              </motion.div>

              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={1}
                className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.12] text-slate-900"
              >
                The original{" "}
                <span className="bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent">
                  one-to-one tutor,
                </span>{" "}
                rebuilt for the age of AI
              </motion.h1>

              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={2}
                className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl"
              >
                The best education has always been one teacher and one student —
                present, adaptive, relentless about mastery. That privilege was
                rationed by cost and geography. CognitLoop builds the same loop
                at scale, and it does not run out of patience.
              </motion.p>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={3}
                className="mt-4 text-base text-slate-500 leading-relaxed max-w-xl"
              >
                CognitLoop K-12 and Padhlo — the same school tutor, two markets.
                US, Canada, and the UK on one side. India, in the mother tongue,
                on the other. Closed beta in November 2026.
              </motion.div>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={4}
                className="mt-9 flex flex-col sm:flex-row gap-3"
              >
                <a
                  href="#newsletter"
                  data-testid="hero-explore-cta"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all hover:scale-[1.03] shadow-lg shadow-orange-500/25"
                >
                  Get early access
                  <ArrowRight className="h-5 w-5" />
                </a>
                <a
                  href="#why"
                  data-testid="hero-mission-cta"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 transition-all hover:scale-[1.03] shadow-sm"
                >
                  Why it's different
                </a>
              </motion.div>
            </div>

            {/* Right — guru-shishya illustration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className="relative flex items-center justify-center"
            >
              {/* Mandala backdrop, very soft */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <MandalaRing size={440} className="opacity-70" />
              </div>

              {/* Warm card frame */}
              <div className="relative w-full rounded-[2rem] overflow-hidden border border-orange-100 shadow-xl shadow-orange-100/40">
                <img
                  src="/hero-tutor.png"
                  alt="One teacher and one student, the original tutoring loop"
                  className="w-full h-[300px] sm:h-[400px] object-cover object-center block"
                  loading="eager"
                />
              </div>
            </motion.div>
          </div>

          {/* Stats bar */}
          <motion.div
            variants={fadeUp}
            custom={5}
            initial="hidden"
            animate="show"
            className="mt-16 sm:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-slate-200 pt-10"
          >
            {stats.map((s, i) => (
              <div key={s.label} className="text-center sm:text-left">
                <div className="text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="mt-1 text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          ORIGIN STORY / MISSION
          ════════════════════════════════════════ */}
      <section
        id="mission"
        className="py-20 sm:py-28 border-y border-slate-200 bg-white overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left — origin text */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-orange-500">
                The origin
              </p>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
                Before classrooms, before textbooks, there was one guru and one student.
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                In the Gurukul tradition, a student lived with their teacher. The
                guru didn't lecture a hall of thirty — they <em>knew</em> the
                student: how they thought, where they got stuck, what analogies
                would land. Every lesson was adapted in real time. Aristotle
                taught Alexander the same way.
              </p>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Then we scaled education by standardising it. We gained reach and
                lost depth. A friend asked for a one-to-one tutor. We built the
                harness once, then wrapped it twice — school for the US, Canada,
                and the UK, and school for India, because the boards, the
                languages, and the regulations are not the same.
              </p>
              <p className="mt-4 text-base text-slate-500 leading-relaxed">
                The same agentic OODA loop — Observe, Orient, Decide, Act — runs
                under every product. Each agent watches how a learner responds,
                orients on their mental model, decides the next move, acts with a
                targeted lesson or correction. It closes the loop the guru closed
                by hand.
              </p>
            </motion.div>

            {/* Right — 3 pillars */}
            <div className="grid gap-5 relative">
              {/* Faint mandala watermark */}
              <div className="absolute -right-16 top-1/2 -translate-y-1/2 pointer-events-none opacity-30">
                <MandalaRing size={300} />
              </div>

              {[
                {
                  icon: Brain,
                  title: "Cognitive-first design",
                  text: "Every product is engineered around the learner's mental model, not the content catalogue.",
                  color: "text-blue-600",
                  bg: "bg-blue-50",
                },
                {
                  icon: RefreshCw,
                  title: "Agentic feedback loops",
                  text: "Sense → predict → correct. The loop runs after every interaction, not at the end of a term.",
                  color: "text-orange-600",
                  bg: "bg-orange-50",
                },
                {
                  icon: Target,
                  title: "Mastery you can prove",
                  text: "Bite-sized, standards-grounded progress. You earned the next step — the diagnostic said so.",
                  color: "text-emerald-600",
                  bg: "bg-emerald-50",
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  className="relative flex gap-4 p-6 rounded-2xl bg-slate-50 border border-slate-200/80"
                >
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${item.bg} ${item.color} border border-slate-200`}>
                    <item.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                    <p className="mt-1 text-slate-600">{item.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          WHY IT'S A GAME CHANGER
          ════════════════════════════════════════ */}
      <section id="why" className="py-20 sm:py-28 relative overflow-hidden">
        {/* Background mandala — very faint, full bleed */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-20">
          <MandalaRing size={900} className="mandala-outer-ring" />
        </div>

        {/* Warm saffron accent blob */}
        <div
          className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-gradient-to-tl from-orange-100/50 to-transparent blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-orange-500">
              Why it's different
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Everything else is content delivery.{" "}
              <span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent">
                This is a tutor.
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              The difference between a textbook and a great teacher isn't more
              content — it's that the teacher watches you, adapts to you, and
              refuses to let you move on until you actually get it. That's what
              agentic loops make possible.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentiators.map((d, i) => (
              <motion.div
                key={d.title}
                variants={fadeUp}
                custom={i % 3}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="group bg-white border border-slate-200/80 rounded-3xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1.5 transition-all duration-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100">
                  <d.icon className="h-5 w-5 text-orange-600" />
                </span>
                <h3 className="mt-5 text-xl font-semibold text-slate-900">{d.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{d.body}</p>
              </motion.div>
            ))}
          </div>

          {/* "The contrast" pull quote */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-16 relative rounded-[2rem] overflow-hidden border border-orange-100 bg-gradient-to-br from-orange-50 via-amber-50/60 to-yellow-50/30 p-8 sm:p-14"
          >
            {/* Background illustration — vedic guru portrait, very faded */}
            <div className="absolute right-0 bottom-0 w-80 opacity-[0.14] pointer-events-none select-none">
              <img src="/hero-tutor.png" alt="" aria-hidden="true" className="w-full h-auto block" />
            </div>

            <div className="relative max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-6">
                The contrast
              </p>
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                    How edtech has worked
                  </p>
                  <ul className="space-y-2.5 text-slate-500">
                    {[
                      "One-size-fits-all video lectures",
                      "Progress = time spent, not mastery proven",
                      "AI bolted onto existing worksheets",
                      "Curriculum as an afterthought",
                      "Teacher as content creator, not expert",
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2 text-sm leading-snug">
                        <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-slate-300 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                        </span>
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3">
                    How CognitLoop works
                  </p>
                  <ul className="space-y-2.5 text-slate-700">
                    {[
                      "Personalised path built from your diagnostic",
                      "Progress = proven mastery, every step",
                      "AI as the agent, syllabus as the ground truth",
                      "Curriculum-locked — no hallucinated lessons",
                      "Teachers earn from quality, not content volume",
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2 text-sm leading-snug">
                        <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                        </span>
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          PRODUCTS
          ════════════════════════════════════════ */}
      <section id="products" className="py-20 sm:py-28 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-orange-600">
              Two products. One loop.
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Closed beta in November 2026.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              CognitLoop K-12 and Padhlo — same OODA harness. One for school in
              the US, Canada, and the UK. One for India, in the mother tongue,
              because the boards and the regulations are different. Subscribe
              below to get a seat in the beta.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {productList.map((p, i) => (
              <motion.button
                key={p.slug}
                type="button"
                onClick={() => navigate(`/${p.slug}`)}
                data-testid={`product-card-${p.slug}`}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className={`group text-left bg-white border ${p.theme.border} rounded-3xl overflow-hidden shadow-sm hover:-translate-y-1.5 transition-all duration-300 ${p.theme.glow} focus:outline-none focus-visible:ring-2 ${p.theme.ring} focus-visible:ring-offset-2`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={p.image}
                    alt=""
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${p.theme.gradient} opacity-20`} />
                </div>
                <div className="p-8 sm:p-10">
                <div className="flex items-start justify-between gap-4">
                  <span
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider border ${p.theme.border} ${p.theme.badgeBg} ${p.theme.text}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${p.theme.dot}`} />
                    {p.comingSoonLabel}
                  </span>
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${p.theme.softBg} ${p.theme.text} transition-transform duration-300 group-hover:rotate-45`}
                  >
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </div>

                <h3 className="mt-6 text-2xl font-bold tracking-tight text-slate-900">
                  {p.name}
                </h3>
                <p className={`mt-1 text-sm font-semibold ${p.theme.text}`}>
                  {p.tagline}
                </p>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  {p.shortDescription}
                </p>

                <span
                  className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${p.theme.text}`}
                >
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          NEWSLETTER CTA
          ════════════════════════════════════════ */}
      <section id="newsletter" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-8 py-14 sm:px-16 sm:py-20"
          >
            {/* Saffron glow inside the dark card */}
            <div
              className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl pointer-events-none"
              aria-hidden="true"
            />
            <div
              className="absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none"
              aria-hidden="true"
            />

            {/* Faint mandala watermark */}
            <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none opacity-10">
              <MandalaRing size={320} />
            </div>

            <div className="relative max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-orange-400 mb-4">
                Be first
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Get beta notes and research drops.
              </h2>
              <p className="mt-4 text-lg text-slate-400">
                Closed beta in November. Be first to know when the loop opens —
                no spam, just the signal.
              </p>
              <form
                onSubmit={handleSubscribe}
                data-testid="home-newsletter-form"
                className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="home-newsletter-input"
                  className="flex-1 px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-orange-400 transition-all"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="home-newsletter-submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all hover:scale-[1.03] disabled:opacity-70 disabled:hover:scale-100 shadow-lg shadow-orange-900/30"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Subscribe <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
